import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { placeReviewAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Star, CheckCircle, XCircle, Trash2, MessageSquare, Clock, Filter } from "lucide-react";

const AdminPlaceReviews = () => {
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  useEffect(() => { fetchReviews(); }, [filter]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      let res;
      if (filter === "pending") {
        res = await placeReviewAPI.getPending(token);
      } else {
        res = await placeReviewAPI.getAll(token);
      }
      let data = res.data;
      if (filter === "approved") data = data.filter(r => r.is_approved);
      setReviews(data);
    } catch { toast.error("Failed to fetch reviews"); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    try { await placeReviewAPI.approve(id, token); toast.success("Review approved"); fetchReviews(); }
    catch { toast.error("Failed to approve"); }
  };

  const handleReject = async (id) => {
    try { await placeReviewAPI.reject(id, token); toast.success("Review rejected"); fetchReviews(); }
    catch { toast.error("Failed to reject"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this review?")) return;
    try { await placeReviewAPI.delete(id, token); toast.success("Review deleted"); fetchReviews(); }
    catch { toast.error("Failed to delete"); }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-place-reviews">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-2">Place Reviews</h1>
          <p className="text-muted-foreground">Moderate visitor reviews for places of interest</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 bg-white rounded-xl p-1.5 shadow-soft w-fit">
          {[
            { id: "pending", label: "Pending", icon: Clock },
            { id: "approved", label: "Approved", icon: CheckCircle },
            { id: "all", label: "All", icon: Filter },
          ].map(tab => (
            <button key={tab.id} onClick={() => setFilter(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === tab.id ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted/50"}`}
              data-testid={`filter-${tab.id}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map(review => (
              <div key={review.id} className={`bg-white rounded-xl p-5 shadow-soft border-l-4 ${review.is_approved ? "border-l-green-500" : "border-l-amber-400"}`} data-testid={`review-item-${review.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">{review.name.charAt(0).toUpperCase()}</div>
                      <div>
                        <p className="font-semibold text-foreground text-sm">{review.name}</p>
                        {review.email && <p className="text-xs text-muted-foreground">{review.email}</p>}
                      </div>
                      <div className="flex gap-0.5 ml-2">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={`w-4 h-4 ${s <= review.rating ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
                        ))}
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ml-auto ${review.is_approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {review.is_approved ? "Approved" : "Pending"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Place: <span className="font-medium text-foreground">{review.place_name || "Unknown"}</span> | {formatDate(review.created_at)}
                    </p>
                    {review.comment && <p className="text-sm text-muted-foreground mt-2 leading-relaxed bg-muted/30 rounded-lg p-3">{review.comment}</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {!review.is_approved && (
                      <Button size="sm" onClick={() => handleApprove(review.id)} className="bg-green-600 hover:bg-green-700 rounded-lg" data-testid={`approve-${review.id}`}>
                        <CheckCircle className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    )}
                    {review.is_approved && (
                      <Button size="sm" variant="outline" onClick={() => handleReject(review.id)} className="rounded-lg" data-testid={`reject-${review.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleDelete(review.id)} className="text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-review-${review.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {filter === "pending" ? "No Pending Reviews" : filter === "approved" ? "No Approved Reviews" : "No Reviews Yet"}
            </h3>
            <p className="text-muted-foreground">
              {filter === "pending" ? "All caught up! No reviews awaiting moderation." : "Reviews submitted by visitors will appear here."}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPlaceReviews;
